import { Position, PlayerType, Player, PhysicsConfig } from './types';

export class InputManager {
  private keys: Set<string> = new Set();
  private lastUpdate: number = 0;
  private readonly MOVEMENT_SPEED = 3;
  private readonly PLATFORMER_CONFIG: PhysicsConfig = {
    moveSpeed: 0.15,
    jumpForce: 0.5, // Positive because UP is positive Y in game space
    gravity: -0.02, // Negative because gravity pulls DOWN
    maxFallSpeed: -0.5, // Negative max fall speed
    groundLevel: 0,
  };
  private readonly deadzone: number;

  constructor(options = { deadzone: 0.1 }) {
    this.deadzone = options.deadzone;
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleBlur);
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.repeat) return;
    this.keys.add(e.code);
  };

  private handleKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private handleBlur = () => {
    this.keys.clear();
  };

  private getGamepadInput(): Position {
    const gamepad = navigator.getGamepads()?.[0];
    if (!gamepad) return { x: 0, y: 0 };

    const axes = {
      x: Math.abs(gamepad.axes[0]) > this.deadzone ? gamepad.axes[0] : 0,
      y: Math.abs(gamepad.axes[1]) > this.deadzone ? -gamepad.axes[1] : 0,
    };

    const dpad = {
      x: gamepad.buttons[15].pressed ? 1 : gamepad.buttons[14].pressed ? -1 : 0,
      y: gamepad.buttons[12].pressed ? 1 : gamepad.buttons[13].pressed ? -1 : 0,
    };

    return {
      x: axes.x || dpad.x,
      y: axes.y || dpad.y,
    };
  }

  getTopdownInput(): Position {
    return {
      x:
        (this.keys.has('ArrowRight') ? 1 : 0) -
        (this.keys.has('ArrowLeft') ? 1 : 0),
      y:
        (this.keys.has('ArrowUp') ? 1 : 0) -
        (this.keys.has('ArrowDown') ? 1 : 0),
    };
  }

  getPlatformerInput(): Position {
    return {
      x: (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0),
      y: this.keys.has('KeyW') ? 1 : 0,
    };
  }

  public update(
    timestamp: number,
    players: Record<PlayerType, Player>
  ): Record<PlayerType, Position | null> {
    const deltaTime = timestamp - this.lastUpdate;
    this.lastUpdate = timestamp;

    return {
      topdown: this.updateTopdownPlayer(players.topdown, deltaTime),
      platformer: this.updatePlatformerPlayer(players.platformer, deltaTime),
    };
  }

  private updateTopdownPlayer(
    player: Player,
    deltaTime: number
  ): Position | null {
    const input = this.getTopdownInput();
    if (input.x === 0 && input.y === 0) return null;

    const moveSpeed = (this.MOVEMENT_SPEED * deltaTime) / 16.67;
    const velocityAmplitude = 0.1;
    const negativeXAxis = false;
    const negativeYAxis = false;
    return {
      x:
        player.position.x +
        input.x * moveSpeed * velocityAmplitude * (negativeXAxis ? -1 : 1),
      y:
        player.position.y +
        input.y * moveSpeed * velocityAmplitude * (negativeYAxis ? -1 : 1),
    };
  }

  private updatePlatformerPlayer(
    player: Player,
    deltaTime: number
  ): Position | null {
    const input = this.getPlatformerInput();

    // Update horizontal velocity
    player.velocity.x = input.x;

    // Handle jump
    if (this.keys.has('KeyW') && player.isGrounded) {
      player.velocity.y = this.PLATFORMER_CONFIG.jumpForce;
      player.isGrounded = false;
    }

    // Apply gravity if in air
    if (!player.isGrounded) {
      player.velocity.y = Math.max(
        player.velocity.y + this.PLATFORMER_CONFIG.gravity,
        this.PLATFORMER_CONFIG.maxFallSpeed
      );
    }

    // Return new position
    return {
      x: player.position.x + player.velocity.x,
      y: player.position.y + player.velocity.y,
    };
  }

  public cleanup() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleBlur);
    this.keys.clear();
  }

  public isKeyPressed(key: string): boolean {
    return this.keys.has(key);
  }

  public getPlatformerConfig() {
    return this.PLATFORMER_CONFIG;
  }

  public updatePlatformerOnly(
    timestamp: number,
    platformer: Player
  ): Position | undefined {
    const dt = this.getDeltaTime(timestamp);
    return this.updatePlatformerPosition(platformer, dt);
  }
}
